import React, { useState } from 'react';
import { XIcon, LinkIcon, CheckIcon, CopyIcon, InfoIcon, ShieldCheckIcon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Switch from '@radix-ui/react-switch';
import { motion, AnimatePresence } from 'framer-motion';
interface AddLearnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  classroomName: string;
  joinCode: string;
  onAddStudents?: (emails: string[]) => Promise<void>;
}
export const AddLearnersModal: React.FC<AddLearnersModalProps> = ({
  isOpen,
  onClose,
  classroomName,
  joinCode
}) => {
  const [copied, setCopied] = useState(false);
  const [requestGuardianInfo, setRequestGuardianInfo] = useState(true);
  const handleCopy = () => {
    navigator.clipboard.writeText(`https://app.ainstein.com/join/${joinCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-57">
      <AnimatePresence data-id="element-58">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-59">
            <Dialog.Overlay asChild data-id="element-60">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-61">
                <Dialog.Content asChild data-id="element-62">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-lg bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative max-h-[90vh]" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-63">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50" data-id="element-64">
                      <div data-id="element-65">
                        <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-66">
                          Enroll Students
                        </Dialog.Title>
                        <Dialog.Description className="text-xs text-text-secondary mt-1" data-id="element-67">
                          Share this link with students to enroll them in{' '}
                          <span className="font-semibold text-text-primary" data-id="element-68">
                            {classroomName}
                          </span>
                        </Dialog.Description>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-69">
                        <XIcon size={20} data-id="element-70" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-5 overflow-y-auto" data-id="element-71">
                      {/* Guardian Info Collection Setting */}
                      <div className="space-y-3" data-id="element-72">
                        <div className="flex items-center justify-between" data-id="element-73">
                          <div className="flex-1" data-id="element-74">
                            <label htmlFor="guardian-info-toggle" className="text-sm font-semibold text-text-primary flex items-center gap-2 cursor-pointer" data-id="element-75">
                              <ShieldCheckIcon size={16} className="text-[#00B75F]" data-id="element-76" />
                              Request Guardian Information
                            </label>
                            <p className="text-xs text-text-secondary mt-1" data-id="element-77">
                              Ask students to provide guardian contact details
                              during enrollment
                            </p>
                          </div>
                          <Switch.Root id="guardian-info-toggle" checked={requestGuardianInfo} onCheckedChange={setRequestGuardianInfo} className={`relative w-11 h-6 rounded-full transition-colors ${requestGuardianInfo ? 'bg-[#00B75F]' : 'bg-gray-200'}`} data-id="element-78">
                            <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform translate-x-0.5 data-[state=checked]:translate-x-[22px]" data-id="element-79" />
                          </Switch.Root>
                        </div>

                        {requestGuardianInfo && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className="bg-blue-50 border border-blue-100 rounded-xl p-3" data-id="element-80">
                            <div className="flex gap-2" data-id="element-81">
                              <InfoIcon size={14} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-82" />
                              <div className="text-xs text-blue-900 leading-relaxed" data-id="element-83">
                                <p className="font-semibold mb-1" data-id="element-84">
                                  How it works:
                                </p>
                                <ul className="space-y-1 list-disc list-inside" data-id="element-85">
                                  <li data-id="element-86">
                                    Students can optionally provide guardian
                                    contact info when enrolling
                                  </li>
                                  <li data-id="element-87">
                                    If skipped, automated reminders will be sent
                                    to complete their profile
                                  </li>
                                  <li data-id="element-88">
                                    Guardian contacts serve as distribution
                                    endpoints for sharing student reports
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </motion.div>}
                      </div>

                      {/* Enrollment Link */}
                      <div className="space-y-2" data-id="element-89">
                        <label className="text-sm font-semibold text-text-primary" data-id="element-90">
                          Enrollment Link
                        </label>
                        <div className="flex gap-2" data-id="element-91">
                          <div className="flex-1 bg-gray-50 border border-custom rounded-xl px-4 py-3 text-sm text-text-primary font-mono truncate" data-id="element-92">
                            https://app.ainstein.com/join/{joinCode}
                          </div>
                          <button onClick={handleCopy} className="px-4 py-2 bg-[#00B75F] text-white rounded-xl font-semibold hover:bg-[#00a055] transition-colors flex items-center gap-2 min-w-[100px] justify-center" data-id="element-93">
                            {copied ? <>
                                <CheckIcon size={16} data-id="element-94" />
                                <span data-id="element-95">Copied</span>
                              </> : <>
                                <CopyIcon size={16} data-id="element-96" />
                                <span data-id="element-97">Copy</span>
                              </>}
                          </button>
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="bg-gray-50 border border-custom rounded-xl p-4" data-id="element-98">
                        <p className="text-xs text-text-secondary leading-relaxed" data-id="element-99">
                          Students click the link, sign in with their email, and
                          are automatically enrolled.
                          {requestGuardianInfo && ' They will be prompted to add guardian information during the enrollment process.'}{' '}
                          Link expires at the end of the academic year.
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 pt-0 border-t border-gray-100 bg-gray-50/30" data-id="element-100">
                      <button onClick={onClose} className="w-full px-4 py-2.5 border-2 border-custom text-text-primary rounded-xl font-semibold hover:bg-surface transition-colors" data-id="element-101">
                        Done
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};